import { clsx } from 'clsx'
import { CalendarDays, Star, Clock, LayoutGrid, AlertTriangle, CheckCircle, ChevronRight } from 'lucide-react'
import { useState } from 'react'

export type QuickFilterType = 'all' | 'today' | 'week' | 'flagged' | 'overdue' | 'high-week' | 'recent-done'

interface QuickFiltersProps {
  active: QuickFilterType
  onChange: (filter: QuickFilterType) => void
}

const primaryFilters = [
  { id: 'all' as const, label: '전체', icon: LayoutGrid },
  { id: 'today' as const, label: '오늘', icon: Clock },
  { id: 'week' as const, label: '이번 주', icon: CalendarDays },
  { id: 'flagged' as const, label: '중요', icon: Star },
]

const presetFilters = [
  { id: 'overdue' as const, label: '기한 지남', icon: AlertTriangle },
  { id: 'high-week' as const, label: '이번 주 높은 우선순위', icon: Star },
  { id: 'recent-done' as const, label: '최근 완료', icon: CheckCircle },
]

export function QuickFilters({ active, onChange }: QuickFiltersProps) {
  const [showPresets, setShowPresets] = useState(false)
  const isPresetActive = presetFilters.some((f) => f.id === active)

  return (
    <div className="flex w-full items-center gap-1.5 overflow-x-auto scrollbar-none" aria-label="빠른 필터">
      {primaryFilters.map((f) => {
        const isActive = active === f.id
        const Icon = f.icon
        return (
          <button
            key={f.id}
            type="button"
            onClick={() => onChange(f.id)}
            aria-pressed={isActive}
            className={clsx(
              'inline-flex min-h-[44px] items-center gap-1.5 rounded-xl px-3 text-label font-medium whitespace-nowrap transition-all',
              isActive
                ? 'workspace-primary-action text-white'
                : 'border border-transparent bg-surface-tertiary text-body hover:border-[var(--workspace-border-strong)] hover:bg-[var(--active-bg)]'
            )}
          >
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {f.label}
          </button>
        )
      })}

      {/* Divider + preset toggle */}
      <div className="w-px h-5 bg-[var(--border-default)] flex-shrink-0" />
      <button
        type="button"
        onClick={() => setShowPresets(!showPresets)}
        aria-expanded={showPresets}
        className={clsx(
          'inline-flex min-h-[44px] items-center gap-1 rounded-xl px-2.5 text-label font-medium whitespace-nowrap transition-colors',
          isPresetActive || showPresets
            ? 'text-primary-500'
            : 'text-caption hover:text-body'
        )}
        aria-label="스마트 필터"
      >
        <ChevronRight className={clsx('w-3 h-3 transition-transform', (showPresets || isPresetActive) && 'rotate-90')} />
        {!showPresets && !isPresetActive && '더보기'}
      </button>

      {(showPresets || isPresetActive) && presetFilters.map((f) => {
        const isActive = active === f.id
        const Icon = f.icon
        return (
          <button
            key={f.id}
            type="button"
            onClick={() => onChange(f.id)}
            aria-pressed={isActive}
            className={clsx(
              'inline-flex min-h-[44px] items-center gap-1.5 rounded-xl px-3 text-label font-medium whitespace-nowrap transition-all',
              isActive
                ? 'workspace-primary-action text-white'
                : 'border border-transparent bg-surface-tertiary text-body hover:border-[var(--workspace-border-strong)] hover:bg-[var(--active-bg)]'
            )}
          >
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {f.label}
          </button>
        )
      })}
    </div>
  )
}
